import IconButton from '@/ctComponents/IconButton';
import React, { useEffect, useState } from 'react';
import {
  getReferredList,
  SaveApiResMessage,
  sendReferralInvite,
} from '@/actions';
import { useNotify } from 'react-admin';
import { Input } from '@/components/Input';
import * as yup from 'yup';
import { ReferralLinkModal } from './referral-link-modal';
import { ReferralHistory } from './history';
import { RewardModal } from './rewards-modal';
import LoadingV2 from '@/components/Loading';

const formSchema = yup.object().shape({
  f_name: yup.string().required('First name is required'),
  l_name: yup.string().required('Last name is required'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  company: yup.string().required('Company is required'),
  phone: yup.string().required('Phone is required'),
});

const DataRow = ({ label, value, isLast = false }) => {
  return (
    <div
      className={`flex justify-between ${
        !isLast
          ? 'border-t-0 border-l-0 border-r-0 border-b border-solid pb-3'
          : ''
      }`}>
      <span className="font-bold text-sm flex items-center">{label}</span>
      <span className="text-sm text-secondary-text">{value}</span>
    </div>
  );
};

export const Referral = ({ setSelectedTab }) => {
  const [referred, setReferred] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [openRewardsModal, setOpenRewardModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [formData, setFormData] = useState({
    f_name: '',
    l_name: '',
    email: '',
    company: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();

  const getReferred = () => {
    setLoading(true);
    getReferredList({ page: 1 })
      .then((res) => {
        if (res.message === 'Complete your profile please.') {
          notify('Complete your profile please.', 'warning');
          if (setSelectedTab) setSelectedTab(0);
        } else {
          setReferred(res.data);
        }
      })
      .catch((err) => {
        notify(err, 'warning');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getReferred();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = async () => {
    try {
      await formSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleViewRewards = () => {
    setOpenRewardModal(true);
  };

  const handleGetReferralLink = () => {
    setShowForm(!showForm);
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSendInvitation = async () => {
    setSubmitting(true);

    try {
      const isValid = await validateForm();

      if (!isValid) {
        notify('Please fix the errors before submitting', 'warning');
        setSubmitting(false);
        return;
      }

      const res = await sendReferralInvite(formData);
      if (!res.errors) {
        // notify('User has been successfully invited', 'success');
        SaveApiResMessage({
          title: res?.data?.messageHeader,
          message: res?.message,
          mode: 'success',
        });
        setFormData({
          f_name: '',
          l_name: '',
          email: '',
          company: '',
          phone: '',
        });
        setErrors({});
        setShowForm(false);
        getReferred();
      } else {
        Object.keys(res.errors).forEach((key) => {
          // notify(res.errors[key][0], 'warning');
          SaveApiResMessage({
            title: 'Warning',
            message: res.errors[key][0],
            mode: 'warning',
          });
        });
      }
    } catch (err) {
      // notify(err, 'warning');
      SaveApiResMessage({
        title: 'Error',
        message: err,
        mode: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const REFERRAL_FIELDS = [
    {
      label: 'Total Invitations',
      name: 'invite',
      value: referred?.invite || 0,
    },
    {
      label: 'Potential Rewards',
      name: 'potential_rewards',
      value: `$${referred?.potential_rewards || 0}`,
    },
    {
      label: 'Pending Invitations',
      name: 'pending_invitations',
      value:
        referred?.referred_users?.filter(
          (user) => user.status_text === 'Pending'
        )?.length || 0,
    },
    {
      label: 'Accepted Invitations',
      name: 'accepted_invitations',
      value:
        referred?.referred_users?.filter(
          (user) => user.status_text === 'Accepted'
        )?.length || 0,
    },
    {
      label: 'You have earned',
      name: 'earned_rewards',
      value: `$${referred?.earned_rewards || 0}`,
    },
  ];

  if (loading) return <LoadingV2 />;

  return (
    <>
      <div className="lg:flex-row flex-col flex  gap-12 space-y-4 lg:space-y-0 pb-4">
        <div className="flex-1 xl:max-w-795">
          <div className="border border-solid pb-4 flex-1 rounded-md p-4">
            <div className="flex justify-between items-center pb-4 ">
              <h2 className="text-lg font-bold m-0 ">
                Refer friends and earn rewards{' '}
              </h2>
              {showForm && (
                <IconButton
                  onClick={() => {
                    setOpenHistoryModal(true);
                  }}
                  text={'History'}
                  icon="timeFile"
                  color={'#131617'}
                  style={{
                    height: 40,
                    borderRadius: 4,
                  }}
                  size={'big'}
                />
              )}
            </div>
            <hr className="mb-4 mt-0 border-t border-solid" />

            <div
              dangerouslySetInnerHTML={{
                __html: referred.current_referral_program.content2,
              }}
              className="text-sm text-secondary-text"
            />

            {showForm && (
              <div className="md:grid grid-cols-2 gap-4 mt-10 space-y-4 md:space-y-0">
                <Input
                  label="First Name"
                  type="text"
                  value={formData.f_name}
                  onChange={(e) => handleInputChange('f_name', e.target.value)}
                  placeholder="Enter First Name"
                  error={errors.f_name}
                  required
                />
                <Input
                  label="Last Name"
                  type="text"
                  value={formData.l_name}
                  onChange={(e) => handleInputChange('l_name', e.target.value)}
                  placeholder="Enter Last Name"
                  error={errors.l_name}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter Email"
                  error={errors.email}
                  required
                />
                <Input
                  label="Company"
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Enter Company Name"
                  error={errors.company}
                  required
                />
                <Input
                  label="Phone Number"
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter Phone Number"
                  error={errors.phone}
                  required
                />
              </div>
            )}
            <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:items-end justify-between mt-12">
              {showForm ? (
                <IconButton
                  onClick={handleSendInvitation}
                  text={'Send Invitation'}
                  color={'#131617'}
                  style={{
                    backgroundColor: '#0E121B',
                    color: 'white',
                    borderRadius: 4,
                    height: 40,
                  }}
                  size={'big'}
                  disabled={submitting}
                  loading={submitting}
                />
              ) : (
                <IconButton
                  onClick={handleGetReferralLink}
                  text={'Get my referral link now'}
                  color={'#131617'}
                  style={{
                    backgroundColor: '#0E121B',
                    color: 'white',
                    height: 40,
                    borderRadius: 4,
                  }}
                  size={'big'}
                />
              )}
              <IconButton
                onClick={() => {
                  setOpenLinkModal(true);
                }}
                text={'Want to get your personal referral link?'}
                color={'##335CFF'}
                size={'big'}
                style={{
                  border: 'none',
                  padding: 0,
                  color: '#335CFF',
                  fontSize: 14,
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex-1">
          {' '}
          <div className="border border-solid pb-4 flex-1 rounded-md">
            <div className="flex justify-between items-center px-4">
              <h2 className="text-lg font-bold ">Your Referrals </h2>
              <IconButton
                onClick={handleViewRewards}
                style={{ fontWeight: 400, height: 40 }}
                icon={'eye'}
                text={'View Rewards'}
                color={'#131617'}
              />
            </div>
            <hr
              className="mb-4 mt-0 border-t border-solid"
              style={{
                width: 'calc(100% - 32px)',
              }}
            />

            <div className="space-y-3 max-h-550 overflow-y-auto px-4">
              {referred ? (
                REFERRAL_FIELDS.map((field, index) => (
                  <DataRow
                    key={index}
                    label={field.label}
                    value={field.value}
                    isLast={index === REFERRAL_FIELDS.length - 1}
                  />
                ))
              ) : (
                <div className="flex justify-center items-center py-8">
                  <div className="text-gray-500">
                    No referral data available
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ReferralLinkModal
        show={openLinkModal}
        setShow={setOpenLinkModal}
        data={referred}
      />
      <ReferralHistory
        show={openHistoryModal}
        setShow={setOpenHistoryModal}
        data={referred?.referred_users || []}
      />
      <RewardModal
        data={referred?.referred_users || []}
        show={openRewardsModal}
        setShow={setOpenRewardModal}
      />
    </>
  );
};
